import React, { Suspense, useCallback, useEffect, useRef } from "react";
import { usePromise } from "react-use";

import { useBusyBlockerContext } from "../../contexts/busy-blocker-context";
import { useAppContext } from "../../hooks/useAppContext";
import { useAutoSave } from "../../hooks/useAutoSave";
import { useDisableImageContextMenu } from "../../hooks/useDisableImageContextMenu";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import { useErrorHandling } from "../../hooks/useErrorHandling";
import { Automation } from "../automation";
import { DialogQueue } from "../dialog-queue";
import { OfflineDialog } from "../dialogs/offline-dialog";
import { GlobalLoading } from "../global-loading";
import { Header } from "../header";
import { NavigationBar } from "../navigation-bar";
import { OrientationBlocker, OrientationBlockerContent } from "../orientation-blocker";
import { PreviewBox } from "../preview-box";
import { SlideshowPreview } from "../slideshow-preview";
import { Snackbar } from "../snackbar";
import { TopToolbar } from "../top-toolbar";
import { useAppUnload } from "./useAppUnload";

import "../../variables.css";
import "../../reset.css";
import "../../scrollbar.css";

import "./app.css";
import { useSetDefinitionFeatures } from "../../hooks/useDefinitionFeatures";
import { useLoginManager } from "../../hooks/useLoginManager";
import { useNavigateToUrl } from "../../hooks/useNavigate";
import { usePerformanceMonitoring } from "../../hooks/usePerformaceMonitoring";
import { useResetDefinitionForDesign } from "../../hooks/useResetDefinitionForDesign";
import { useResetDefinitionForOptions } from "../../hooks/useResetDefinitionForOptions";
import { useReuseZonesForPages } from "../../hooks/useReuseZonesForPages";
import { useReuseZonesForTemplates } from "../../hooks/useReuseZonesForTemplates";
import { TooltipLayer } from "../tooltipOld";
import { labels } from "./labels";
import { useFeatureFlag, useFeatureFlagToggle } from "../../hooks/useFeatureFlags";
import { useKeyDown } from "../../hooks/useKeyboard";
import { useShowDesktopLayout } from "../../hooks/useShowdesktopLayout";
import { Sidebar } from "../sidebar-menus/sidebar/sidebar";
import { TopMenu } from "../top-menus/top-menu/top-menu";
import AddToCartButton from "../header/add-to-cart-button";
import { useIsPreviewMode } from "../../hooks/useIsPreviewMode";
import { useIsMobileDevice } from "../../hooks/useIsMobileDevice";
import { useVisibilityChange } from "../../hooks/useVisibilityChange";
import { ViewMenu } from "../view-menu/view-menu";
import { useDashboardContext } from "../../contexts/dashboard-context";
import { useIsTransformMode } from "../../hooks/useIsTransformMode";
import { menusState } from "../../hooks/useMenus";
import { useMessage } from "../../hooks/useMessage";
import { useTextZoneDashboard } from "../../hooks/useTextZoneDashboard";
import { getDefaultMenuForZone } from "../bottom-menu/menu-logic";

const BottomMenu = React.lazy(
	() => import(/* webpackChunkName: "express-bottom-menu" */ "../bottom-menu"),
);

function useUpdateMenuOnMessage() {
	const {
		editor: { selection },
	} = useAppContext();
	const dashboard = useDashboardContext();
	const textDashboard = useTextZoneDashboard();
	const [isTransformMode] = useIsTransformMode();
	const showDesktopLayout = useShowDesktopLayout();
	const prevZoneRef = useRef<eXpress.core.IZone | undefined>(selection.getCurrentZone());

	// Used useMessage instead of useEffect
	// Reason: We only want it to trigger on selection change, not if one of the dependencies of useEffect changes
	useMessage(
		useCallback(
			(message) => {
				if (message instanceof eXpress.core.DefinitionSwitchedMessage) {
					return;
				}

				// On selection change, try to keep current menu open
				if (
					eXpress.core.isSelectedZoneChanged(message) ||
					eXpress.core.isSelectedSpreadChanged(message) || // When going from no zone to other spread (no zone), the other spread can have other rules that the current menu cannot stay open)
					message instanceof eXpress.core.CurrentZoneTouchedMessage
				) {
					const zone = selection.getCurrentZone();
					const prevZone = prevZoneRef.current;
					const page = selection.getLogicalSelectedPage();
					const newMenuId = getDefaultMenuForZone(
						// Get latest value, not react state that can still have an old value when called twice
						// Otherwise: when deselection a photo zone, this is called bu menuId had still the old value
						menusState.menuId,
						zone,
						page,
						{
							main: dashboard,
							text: textDashboard,
						},
						isTransformMode,
						zone === prevZone ? zone : undefined, // pass undefined if we don't now clicked zone zone
						showDesktopLayout,
					);
					prevZoneRef.current = zone;

					menusState.actions.setMenu(newMenuId);
				}
			},
			[dashboard, selection, isTransformMode, textDashboard, showDesktopLayout],
		),
	);
}

export function App() {
	// Extract info from context
	const appContext = useAppContext();
	const { busyBlocker } = useBusyBlockerContext();
	const { createFatalErrorHandler, createErrorHandler } = useErrorHandling();
	const loginManager = useLoginManager();
	const featureAddMapZones = useFeatureFlagToggle("add2MapZonesPerPage");
	const featureDefaultMapStyle = useFeatureFlag("addDefaultMapStyle", "");
	const featureDefaultMapLocation = useFeatureFlag("addDefaultMapLocation", "");
	const showDesktopLayout = useShowDesktopLayout();

	useDisableImageContextMenu();
	useDocumentTitle();
	useSetDefinitionFeatures();
	useReuseZonesForTemplates();
	useReuseZonesForPages();
	useResetDefinitionForDesign();
	useResetDefinitionForOptions();
	useUpdateMenuOnMessage();

	const [isPreviewMode] = useIsPreviewMode();
	const isMobileDevice = useIsMobileDevice();

	const {
		editor,
		getFontSizeCorrectorAsync,
		localizedContent,
		platform: { queryString },
		serverLogger,
	} = appContext;

	const resolveIfMounted = usePromise();

	// Prevent default behavior on save
	// The real save handler is conditional in another component
	useKeyDown(
		(event) => {
			if (event.key === "s" && (event.ctrlKey || event.metaKey)) {
				event.preventDefault();
			}
		},
		{
			capture: true, // Also execute for items that call stopPropagation
			enableOnFormTags: true, // Example: needed to disable save on during text edit
			ignoreTrap: true, // Always prevent default browser keys. Example: Ctrl+S
		},
	);

	usePerformanceMonitoring();
	const navigateToUrl = useNavigateToUrl();

	// Onload of App
	useEffect(
		() => {
			const fetchKeysAsync = async () => {
				// Preload all labels
				const keys = eXpress.creator
					.getLabelKeys(labels) // Flatten nested structure
					.map(
						(
							labelRequestOrString, // Only the keys needed
						) =>
							eXpress.utils.isString(labelRequestOrString)
								? labelRequestOrString
								: labelRequestOrString.label,
					);

				await localizedContent.prefetchLabelsAsync(keys).catch(createErrorHandler());
			};

			const getDefinitionRequestFromQueryString = ():
				| eXpress.core.DefinitionRequest
				| undefined => {
				const productVariantCodeString = queryString.getQueryStringParameter("pvc");
				const productVariantCode = productVariantCodeString
					? eXpress.ProductVariantCode.fromString(productVariantCodeString)
					: undefined;
				const predefinedProductNameString = queryString.getQueryStringParameter("des");
				const predefinedProductName = predefinedProductNameString
					? eXpress.PredefinedProductName.fromString(predefinedProductNameString)
					: undefined;

				return predefinedProductName && productVariantCode
					? {
							predefinedProductName,
							productVariantCode,
					  }
					: undefined;
			};

			const loadDefinitionAsync = async () => {
				const definitionId = queryString.getQueryStringParameter("def");
				const definitionRequest: eXpress.core.DefinitionRequest | undefined = definitionId
					? eXpress.DefinitionId.fromString(definitionId)
					: // Create Definition
					  getDefinitionRequestFromQueryString();

				if (!definitionRequest) return;
				if (definitionId) {
					await loginManager.waitForLogin();
				}

				let error: eXpress.Exception | undefined = undefined;
				const definitionData = editor
					.loadDefinitionAsync(
						definitionRequest,
						getFontSizeCorrectorAsync,
						// React.StrictMode will run some functions twice causing definition to be loaded twice (Only run at start error)
						// TODO: (React 18) components should be resilient to effects being mounted and destroyed multiple times
						true,
					)
					.then((definition) => {
						if (featureAddMapZones) {
							// Set only first 2 photo zone as map zone
							definition.getUsedPages().forEach((pageZone) => {
								const zone = pageZone.photoZones.at(0);
								if (!zone) return;
								if (!zone.metaData) zone.metaData = {};
								zone.metaData.AllowedPhotoSourceTypes = "map";

								const zone2 = pageZone.photoZones.at(1);
								if (!zone2) return;
								if (!zone2.metaData) zone2.metaData = {};
								zone2.metaData.AllowedPhotoSourceTypes = "map";
							});
						}
						if (featureDefaultMapStyle) {
							definition.metaData ??= {};
							definition.metaData["Map.MapStyle"] = featureDefaultMapStyle;
						}
						if (featureDefaultMapLocation) {
							definition.metaData ??= {};
							definition.metaData["Map.Location"] = featureDefaultMapLocation;
						}

						return definition;
					})
					.catch((err: unknown) => {
						error = err as eXpress.Exception;
					});

				const [definition] = await Promise.all([definitionData, fetchKeysAsync()]);
				// Prevent throwing unneccesairy error just before redirecting to login
				if (!definition) {
					if (error) throw error;
					throw new Error("No product specified in querystring to load.");
				}
			};

			resolveIfMounted(busyBlocker.while(loadDefinitionAsync)).catch((e: unknown) => {
				// When the page is refreshed (or browser back to this page) it can happen that the browser uses a cached page
				// If the user has no or expired authenication cookie, we can start the application without being authenticated
				if (e instanceof eXpress.api.NotAuthenticatedException) {
					editor.logger.log(
						"No authentication cookie (accId) found anymore. Redirecting to login page.",
					);
					navigateToUrl(
						`/login.aspx?returnurl=${encodeURIComponent(window.location.href)}`,
						{
							waitForPendingUploads: "none",
						},
					);
					// Don't throw error for redirect/operation abort since this means the user is already redirected or navigated away
				} else if (
					!(
						e instanceof eXpress.RedirectException ||
						e instanceof eXpress.OperationAbortedException
					)
				) {
					createFatalErrorHandler()(e);
				}
			});
		},
		// this function should NEVER be rerun, all dependencies should be immutable
		// eslint-disable-next-line react-compiler/react-compiler
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	);

	useEffect(() => {
		const onPageShow = (event: PageTransitionEvent) => {
			const historyTraversal =
				event.persisted ||
				(typeof window.performance !== "undefined" &&
					// eslint-disable-next-line @typescript-eslint/no-deprecated
					window.performance.navigation.type === 2);

			if (historyTraversal) {
				document.location.reload();
			}
		};

		window.addEventListener("pageshow", onPageShow);

		return () => {
			window.removeEventListener("pageshow", onPageShow);
		};
	}, []);

	useAutoSave();
	useAppUnload();

	// Try to send any remaining logs when page is closed
	// Using visibility changes is preferred over '(before)unload' events
	useVisibilityChange((isVisible) => {
		if (!isVisible) {
			// When page is closed, can we still flush logs?
			serverLogger.flush();
		}
	});

	return (
		<>
			<div className="app">
				<SlideshowPreview />
				<div className="app__header">
					<Header />
				</div>
				<div className="app__sidebar">{showDesktopLayout ? <Sidebar /> : null}</div>
				<div className="app__content">
					{showDesktopLayout ? <TopMenu /> : null}
					{showDesktopLayout ? null : <TopToolbar />}
					{/* Box measuring the location for the preview */}
					<PreviewBox />
					<NavigationBar />
					{showDesktopLayout ? <ViewMenu /> : null}
				</div>
				<div className="app__footer	">
					{!showDesktopLayout ? (
						<Suspense fallback={<GlobalLoading />}>
							{/* fullscreen blocking was needed: CRE-3348 */}
							<BottomMenu />
						</Suspense>
					) : null}
					{isPreviewMode && isMobileDevice && (
						<AddToCartButton
							showLargeButton={true}
							className="app__add-to-cart-button"
						/>
					)}
				</div>
			</div>
			<Snackbar />
			<DialogQueue />
			<OfflineDialog />
			<OrientationBlocker>
				<OrientationBlockerContent />
			</OrientationBlocker>
			<TooltipLayer />
			<Automation />
		</>
	);
}
